import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/atoms';
import { FormField } from '../../components/molecues/FormField';
import { useTvUserOnboarding } from '../../hooks/useTvUserOnboarding';
import type { TvUserOnboardingRequest } from '../../types/api/tv-user';
import type { UserGender } from '../../types/api/admin-user';
import { USER_GENDERS } from '../../types/api/admin-user';
import toast from 'react-hot-toast';

const createDefaultFormData = (): TvUserOnboardingRequest => ({
  name: '',
  email: '',
  password: '',
  phone: '',
  dateOfBirth: null,
  gender: 'PREFER_NOT_TO_SAY',
});

/**
 * TV User Onboarding Page
 *
 * Admin page for onboarding TV platform users.
 * Creates a new TV user account with the provided credentials.
 */
export const TvUserOnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const { onboardTvUser } = useTvUserOnboarding();

  const [formData, setFormData] = useState<TvUserOnboardingRequest>(createDefaultFormData());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (field: keyof TvUserOnboardingRequest, value: string | null) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2 || formData.name.trim().length > 100) {
      newErrors.name = 'Name must be between 2 and 100 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    }

    if (formData.dateOfBirth) {
      const inputDate = new Date(formData.dateOfBirth);
      const now = new Date();
      if (Number.isNaN(inputDate.getTime())) {
        newErrors.dateOfBirth = 'Invalid date';
      } else if (inputDate > now) {
        newErrors.dateOfBirth = 'Date of birth cannot be in the future';
      }
    }

    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const payload: TvUserOnboardingRequest = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        password: formData.password,
        phone: formData.phone.trim(),
        dateOfBirth: formData.dateOfBirth?.trim() || null,
        gender: formData.gender,
      };

      const result = await onboardTvUser(payload);
      toast.success(
        `TV user created successfully. User ID: ${result.userId}`
      );
      setFormData(createDefaultFormData());
      setErrors({});
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to onboard TV user';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="text-left">
          <h1 className="text-lg font-bold text-gray-900">TV User Onboarding</h1>
          <p className="text-gray-600 mt-1">
            Create a new TV platform user account
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate('/admin/users')}
          disabled={isSubmitting}
        >
          Back to User Management
        </Button>
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6"
      >
        <div className="space-y-4">
          <h2 className="text-base font-semibold text-gray-900 border-b border-gray-200 pb-2 text-left">
            User Details
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              label="Name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              error={errors.name}
              required
              placeholder="Enter user name"
            />

            <FormField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              error={errors.email}
              required
              placeholder="Enter email address"
            />
          </div>

          <FormField
            label="Password"
            type="password"
            value={formData.password}
            onChange={(e) => handleChange('password', e.target.value)}
            error={errors.password}
            required
            placeholder="Enter password (min 8 characters)"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              label="Phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              error={errors.phone}
              required
              placeholder="e.g. +959987654321"
            />

            <FormField
              label="Date of Birth"
              type="date"
              value={formData.dateOfBirth ?? ''}
              onChange={(e) =>
                handleChange('dateOfBirth', e.target.value || null)
              }
              error={errors.dateOfBirth}
              placeholder="Optional"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Gender <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.gender}
              onChange={(e) =>
                handleChange('gender', e.target.value as UserGender)
              }
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
            >
              {USER_GENDERS.filter((g) => g.value !== '').map((gender) => (
                <option key={gender.value} value={gender.value}>
                  {gender.label}
                </option>
              ))}
            </select>
            {errors.gender && (
              <p className="text-sm text-red-600 mt-1.5">{errors.gender}</p>
            )}
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/users')}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={isSubmitting}>
            Create TV User
          </Button>
        </div>
      </form>
    </div>
  );
};

export default TvUserOnboardingPage;
